import Component from '@glimmer/component'
import { service } from '@ember/service'
import { on } from '@ember/modifier'
import { or, eq, not, and } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import { pick } from 'teacher-ui-client/helpers/pick'
import type { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type { AdditionalGradeSelectOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import { BaseSelect } from 'teacher-ui-client/components/base/select'

interface Signature {
  Element: HTMLSelectElement
  Args: {
    placeholder?: string
    additionalOptions?: AdditionalGradeSelectOption[]
    gradePositions: number[]
    gradePosition?: number
    updateGradePosition: (gradePosition: number) => unknown
    useFullGradeName?: boolean
    themeKey?: ThemeKeys
  }
  Blocks: {
    default: []
  }
}

/**
 * A themed select component.
 * Uses native select hence does not support styling of options.
 *
 * Interface with this component using gradeposition numbers. The component will handle converting between strings for
 * use in the select.
 */
export class GradeSelect extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  get gradePositionOptions() {
    return this.args.gradePositions.map((gradePosition) => {
      const grade = this.gradeSets.findByPosition(gradePosition)
      return {
        id: String(gradePosition), // select's work against strings
        label: this.args.useFullGradeName ? grade?.fullName : grade?.name,
      }
    })
  }

  /**
   * Convert from 'number | undefined' to 'string | undefined', being mindful of 0
   */
  get gradePosition() {
    return typeof this.args.gradePosition === 'number' ? String(this.args.gradePosition) : this.args.gradePosition
  }

  updateGradePosition = (gradePosition: string) => {
    this.args.updateGradePosition(Number(gradePosition))
  }

  <template>
    <BaseSelect
      data-test-grade-select
      @themeKey={{@themeKey}}
      {{on "input" (pick "target.value" this.updateGradePosition)}}
      ...attributes
    >
      {{#if (and @placeholder (not this.gradePosition))}}
        <option value="" selected={{not this.gradePosition}} disabled={{this.gradePosition}}>{{@placeholder}}</option>
      {{/if}}
      {{#each @additionalOptions as |optionItem|}}
        <option value={{optionItem.id}} selected={{eq this.gradePosition optionItem.id}}>
          {{or optionItem.label (if optionItem.labelKey (t optionItem.labelKey))}}
        </option>
      {{/each}}
      <optgroup label={{t "base.select.gradePositions"}}>
        {{#each this.gradePositionOptions as |gradeOption|}}
          <option value={{gradeOption.id}} selected={{eq this.gradePosition gradeOption.id}}>
            {{gradeOption.label}}
          </option>
        {{/each}}
      </optgroup>
    </BaseSelect>
  </template>
}

export default GradeSelect
